import React, { useEffect, useMemo, useState } from "react";
import "../../styles/clientPage.css";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartData,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useLocation, useNavigate } from "react-router-dom";
import "../../styles/common.css";
import { formatCurrency } from "../../utils/utils";
import { format, subDays } from "date-fns";
import SlideOver from "../Common/SlideOver";
import ClientRatingOverview from "./ClientRatingOverview";
import { getPresetDateRange } from "../../utils/utils";

ChartJS.register(Title, Tooltip, Legend, ArcElement, ChartDataLabels);

interface ClientHealthProps {
  ratedClientsData: any;
  showSpinner?: boolean;
  parentPageIdentifier?: string;
  userEmail?: string;
}

type ClientHealthStats = {
  promoterPercCount: number;
  neutralPercCount: number;
  detractorPercCount: number;
  notRatedPercCount: number;
  promoterPercRevenue: number;
  neutralPercRevenue: number;
  detractorPercRevenue: number;
  nullMrrClientPercRevenue: number;
  promoterRevenueAmt: number;
  neutralRevenueAmt: number;
  detractorRevenueAmt: number;
  nullMrrClientCount: number;
  promoterCount: number;
  neutralCount: number;
  detractorCount: number;
  notRatedCount: number;
};

const initialStats: ClientHealthStats = {
  promoterPercCount: 0,
  neutralPercCount: 0,
  detractorPercCount: 0,
  notRatedPercCount: 0,
  promoterPercRevenue: 0,
  neutralPercRevenue: 0,
  detractorPercRevenue: 0,
  nullMrrClientPercRevenue: 0,
  promoterRevenueAmt: 0,
  neutralRevenueAmt: 0,
  detractorRevenueAmt: 0,
  nullMrrClientCount: 0,
  promoterCount: 0,
  neutralCount: 0,
  detractorCount: 0,
  notRatedCount: 0,
};

const ClientHealth: React.FC<ClientHealthProps> = ({ ratedClientsData, showSpinner: showSpinnerProp, parentPageIdentifier, userEmail }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [viewMode, setViewMode] = useState<"count" | "revenue">("count");
  const [isSlideOverOpen, setIsSlideOverOpen] = useState( false);
  const slideOverParent = "client-health";

  const incomingSlideOverParent = location.state?.slideOverParent;

  const lastThreeMonths = useMemo(() => getPresetDateRange("last3MonthsFromYesterday"), []);
  const defaultDateRange = {
    startDate: lastThreeMonths.start_time,
    endDate: lastThreeMonths.end_time,
  };
  const savedFilters = localStorage.getItem("businessOverviewFilters");
  const parsedFilters = savedFilters ? JSON.parse(savedFilters) : {};
  const initialDateRange =
    location.state?.customDateRange ??
    parsedFilters?.customDateRange ??
    defaultDateRange;

  const initialSelectedNps =
    location.state?.selectedNps ??
    parsedFilters?.selectedNps ;
  const [customDateRange, setCustomDateRange] = useState<{ startDate: string; endDate: string } | undefined>(
    initialDateRange
  );
  const [selectedNps, _setSelectedNps] = useState<string | null>(initialSelectedNps);

  useEffect(() => {
    localStorage.setItem(
      "businessOverviewFilters",
      JSON.stringify({
        customDateRange,
        selectedNps,
      })
    );
  }, [customDateRange, selectedNps]);

  useEffect(() => {
    if (incomingSlideOverParent === slideOverParent) {
      setIsSlideOverOpen(true);
    }
    setCustomDateRange(initialDateRange);
  }, [incomingSlideOverParent ]);
  const [stats, setStats] = useState<ClientHealthStats>(initialStats);
  const pieData = ratedClientsData?.businessHealthChart ?? null;
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);

  useEffect(() => {
    if (!pieData) {
      setStats(initialStats);
      setIsDataLoaded(false);
      return;
    }
  
    const statsObj: ClientHealthStats = {
      promoterPercCount: pieData.promoterPercentage ?? 0,
      neutralPercCount: pieData.neutralPercentage ?? 0,
      detractorPercCount: pieData.detractorPercentage ?? 0,
      notRatedPercCount: pieData.notRatedPercentage ?? 0,
  
      promoterPercRevenue: pieData.promoterMrrPercentage ?? 0,
      neutralPercRevenue: pieData.neutralMrrPercentage ?? 0,
      detractorPercRevenue: pieData.detractorMrrPercentage ?? 0,
      nullMrrClientPercRevenue: pieData.noMrrPercantage ?? 0,
  
      promoterRevenueAmt: pieData.promoterMrr ?? 0,
      neutralRevenueAmt: pieData.neutralMrr ?? 0,
      detractorRevenueAmt: pieData.detractorMrr ?? 0,
      nullMrrClientCount: pieData.noMrr ?? 0,
  
      promoterCount: pieData.promoterCount ?? 0,
      neutralCount: pieData.neutralCount ?? 0,
      detractorCount: pieData.detractorCount ?? 0,
      notRatedCount: pieData.notRatedCount ?? 0,
    };
  
    setStats(statsObj);
    setIsDataLoaded(true);
  }, [pieData]);  

  // useEffect(() => {
  //   setSelectedNps(null); // recalc majority for new viewMode
  // }, [viewMode]);

  const shouldShowSpinner = typeof showSpinnerProp === "boolean" ? showSpinnerProp : !isDataLoaded;
  const pPct = viewMode === "revenue" ? Math.round(stats.promoterPercRevenue) : Math.round(stats.promoterPercCount);
  const nPct = viewMode === "revenue" ? Math.round(stats.neutralPercRevenue) : Math.round(stats.neutralPercCount);
  const dPct = viewMode === "revenue" ? Math.round(stats.detractorPercRevenue) : Math.round(stats.detractorPercCount);
  const nrPct = viewMode === "revenue" ? Math.round(stats.nullMrrClientPercRevenue) : Math.round(stats.notRatedPercCount);

  const totalCount = (stats.promoterCount || 0) + (stats.neutralCount || 0) + (stats.detractorCount || 0) + (stats.notRatedCount || 0);
  const totalRevenue =
  (stats.promoterRevenueAmt || 0) +
  (stats.neutralRevenueAmt || 0) +
  (stats.detractorRevenueAmt || 0);
  const noDataForCurrentView = (viewMode === "count" && totalCount === 0) || (viewMode === "revenue" && totalRevenue === 0);

  const majority = useMemo(() => {
    // If hovering → show hovered
    if (hoveredLegend === "promoters") return { v: pPct, l: "Promoter" };
    if (hoveredLegend === "neutral") return { v: nPct, l: "Neutral" };
    if (hoveredLegend === "detractors") return { v: dPct, l: "Detractor" };
    if (hoveredLegend === "notRated" || hoveredLegend === "noRevenue")
      return { v: nrPct, l: viewMode === "revenue" ? "No Revenue" : "Not Rated" };
  
    // Else → show overall majority
    const max = Math.max(pPct, nPct, dPct, nrPct);
    if (max === pPct) return { v: pPct, l: "Promoter" };
    if (max === nPct) return { v: nPct, l: "Neutral" };
    if (max === dPct) return { v: dPct, l: "Detractor" };
    return { v: nrPct, l: viewMode === "revenue" ? "No Revenue" : "Not Rated" };
  }, [pPct, nPct, dPct, nrPct, hoveredLegend, viewMode]);     


  const customCenterTextPlugin: any = useMemo(() => {
    return {
      id: "customCenterText",
      beforeDraw(chart: any) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        const majorityValue = majority.v;
        const majorityLabel = majority.l;

        const centerX = chartArea.left + chartArea.width / 2;
        const centerY = chartArea.top + chartArea.height * 0.72;

        const displayValue =
          typeof majorityValue === "number" && !isNaN(majorityValue)
            ? `${Math.round(majorityValue)}%`
            : "0%";

        const displayLabel = majorityLabel || "Promoter";

        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        ctx.font = '600 44px "Inter"';
        ctx.fillStyle = "#000";
        ctx.fillText(displayValue, centerX, centerY - 12);

        ctx.font = '500 18px "Inter"';
        ctx.fillStyle = "#666";
        ctx.fillText(displayLabel, centerX, centerY + 24);

        ctx.restore();
      },
    };
  }, [majority]);

  const basePieOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 8, bottom: 8 } },
      rotation: -90,
      circumference: 180,
      cutout: "83%",
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ti: any) => {
              const v = ti.dataset.data[ti.dataIndex];
              return `${v}%`;
            },
          },
        },
        datalabels: {
          display: false,
          formatter: (value: any) => `${value}%`,
        },
      },
      onClick: (_e: any, elements: any[]) => {
        if (!elements || elements.length === 0) return;
        const idx = elements[0].index;
        const map = [
          { rating: "Promoter" },
          { rating: "Neutral" },
          { rating: "Detractor" },
          viewMode === "revenue"
            ? { mrr: { operator: "lt", value: 1 } }
            : { rating: "NotRated" },
        ];
        const selected = map[idx];

        navigate("/accounts/customer", {
          state: {
            ...selected,
            status: ["active"],
            isInteractionPresent: true,
          },
        });
      },
    };
  }, [selectedNps, viewMode]);

  const pieChartData = useMemo(() => {
    const baseColors = ["#32BD00", "#FFCD2A", "#F4442F", "#EFEBFB"];
    const map = [
      "promoters",
      "neutral",
      "detractors",
      viewMode === "revenue" ? "noRevenue" : "notRated",
    ];
  
    const backgroundColor = map.map((key, idx) => {
      if (!hoveredLegend) return baseColors[idx];
      return key === hoveredLegend ? baseColors[idx] : "#ffffff";
    });
  
    const offsets = map.map((key) =>
      hoveredLegend && key === hoveredLegend ? 12 : 0
    );
  
    return {
      labels: ["Promoters", "Neutral", "Detractors", viewMode === "revenue" ? "No Revenue" : "Not Rated"],
      datasets: [
        {
          data: [pPct || 0, nPct || 0, dPct || 0, nrPct || 0],
          backgroundColor,
          borderColor: "transparent",
          borderWidth: 3,
          borderAlign: "inner" as const,
          borderJoinStyle: "round" as const,
          offset: offsets, // 👈 this makes hovered slice pop out
        } as any,
      ],
    };
  }, [pPct, nPct, dPct, nrPct, hoveredLegend, viewMode]);  
  const legendItems = [
    {
      key: "promoters",
      label: "Promoter",
      color: "#32BD00",
      perc: pPct,
      value: viewMode === "revenue" ? formatCurrency(stats.promoterRevenueAmt) : stats.promoterCount,
      isMoney: viewMode === "revenue",
    },
    {
      key: "neutral",
      label: "Neutral",
      color: "#FFCD2A",
      perc: nPct,
      value: viewMode === "revenue" ? formatCurrency(stats.neutralRevenueAmt) : stats.neutralCount,
      isMoney: viewMode === "revenue",
    },
    {
      key: "detractors",
      label: "Detractor",
      color: "#F4442F",
      perc: dPct,
      value: viewMode === "revenue" ? formatCurrency(stats.detractorRevenueAmt) : stats.detractorCount,
      isMoney: viewMode === "revenue",
    },
    {
      key: viewMode === "revenue" ? "noRevenue" : "notRated",
      label: viewMode === "revenue" ? "No Revenue" : "Not Rated",
      color: "#EFEBFB",
      perc: nrPct,
      value: viewMode === "revenue" ? stats.nullMrrClientCount : stats.notRatedCount,
      isMoney: viewMode === "revenue",
    },
  ];

  const formatLegendValue = (val: string | number, isMoney: boolean) => {
    if (isMoney) return val as string;
    return Number(val || 0).toLocaleString();
  };
  const yesterday = format(subDays(new Date(), 1), "MMM d, yyyy");

  const shimmerPieData: ChartData<"pie", number[], string> = {
    labels: ["Loading"],
    datasets: [
      {
        data: [100],
        backgroundColor: ["#e2dcdc"], // use a static gray here
        borderWidth: 0,
      },
    ],
  };

  const shimmerPieOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 8, bottom: 8 } },
      rotation: -90,
      circumference: 180,
      cutout: "83%",
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
      },
      animation: false,
      events: [],
    };
  }, []);
  
  
  
  return (
    <div className="section">
      <div className="section__header">
        <h2 className="section__title">Business Health 
          <span className="section__title_sub">(As on {yesterday})</span>
        </h2>
      </div>

      <div className="health-chart-container health-chart-container--row">
          <>
          <div className="chart-wrapper">
              {shouldShowSpinner ? (
                <Pie
                  key={`shimmer-${viewMode}`}
                  data={shimmerPieData}
                  options={shimmerPieOptions as any}
                />
              ) : noDataForCurrentView ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 16, fontWeight: 600 }}>
                    {viewMode === "revenue" ? "No Revenue Available" : "No Data Available"}
                  </span>
                </div>
              ) : (
                <Pie
                  key={`${viewMode}-${majority.l}-${majority.v}`}
                  data={pieChartData}
                  options={basePieOptions as any}
                  plugins={[customCenterTextPlugin]}
                />
              )}
            </div>

            <div className="chart-controls chart-controls--side">
              <div className="mode-toggle mode-toggle--vertical">
                <label>
                  <input
                    type="radio"
                    name="viewMode"
                    value="count"
                    checked={viewMode === "count"}
                    onChange={() => setViewMode("count")}
                  />
                  <span className="mode-label">Count</span>
                </label>

                <label>
                  <input
                    type="radio"
                    name="viewMode"
                    value="revenue"
                    checked={viewMode === "revenue"}
                    onChange={() => setViewMode("revenue")}
                  />
                  <span className="mode-label">Revenue</span>
                </label>
              </div>
            </div>
          </>
      </div>

      <div className="legend-box">
      {shouldShowSpinner ? (
          <div className="shimmer-legend-grid" style={{width: "100%"}}>
            {legendItems.map((_, idx) => (
              <div key={`shimmer-legend-${idx}`} className="shimmer-legend-item">
                <div className="shimmer-legend-top">
                  <span className="shimmer-legend-color shimmer-box" />
                  <span className="shimmer-legend-value shimmer-box" />
                </div>
                <span className="shimmer-legend-text shimmer-box" />
              </div>
            ))}
          </div> 
        ) : (
        legendItems.map((item) => (
          <div key={item.key} className="legend-item"
          style={{ cursor: "pointer",
            opacity: !hoveredLegend || hoveredLegend === item.key ? 1 : 0.4,
            transition: "opacity 0.2s",
           }}
           onMouseEnter={() => setHoveredLegend(item.key)}
           onMouseLeave={() => setHoveredLegend(null)}
          onClick={() => {
          const isNoRevenue = item.key === "noRevenue";

          const ratingMap: Record<string, string> = {
            promoters: "Promoter",
            neutral: "Neutral",
            detractors: "Detractor",
            notRated: "NotRated",
          };

          navigate("/accounts/customer", {
            state: {
              ...(isNoRevenue
                ? {
                    mrr: { operator: "lt", value: 1 },
                    status: ["active"],
                    isInteractionPresent: true,
                  }
                : {
                    rating: ratingMap[item.key],
                    status: ["active"],
                    isInteractionPresent: true,
                  }),
            },
          });
        }}
          >
            <div className="legend-color-label">
            <span
              className="legend-color"
              style={{ backgroundColor: item.color, width: 14, height: 14, display: "inline-block", borderRadius: 3 }}
            />
            <span className="legend-label" style={{ color: "#333" }}>
            {item.key === "noRevenue"
          ? `${item.value} Clients`
          : `${Math.round(item.perc || 0)}% [${formatLegendValue(item.value as number, item.isMoney)}]`}
            </span>
          </div>
          <div className="legend-label">{item.label}</div>
          </div>
        )
        ))}
      </div>
      <SlideOver isOpen={isSlideOverOpen} onClose={() => setIsSlideOverOpen(false)}>
        {
          isSlideOverOpen && (
            <ClientRatingOverview isSlideOverOpen = {isSlideOverOpen}
            selectedNps={selectedNps} viewMode={viewMode} slideOverParent={slideOverParent}
            parentPageIdentifier={parentPageIdentifier} userEmail={userEmail}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange} 
            />
          )}
      </SlideOver>
    </div>
  );
};

export default ClientHealth;
